import { ElementType, memo, useEffect, useRef } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  AvailabilityResponse,
  PlanningEntityIdentifier,
  PlanningEntityType
} from 'generated/index';

import { PRIMARY, RED } from 'constants/colors';
import { brightness } from 'utils/misc';

enum AvailabilityType {
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  POSSIBLE = 'POSSIBLE',
  AVAILABLE = 'AVAILABLE',
  UNSPECIFIED = 'UNSPECIFIED'
}

interface AvailabilityPragmaticProps {
  id: string;
  availability: AvailabilityResponse;
  component?: ElementType<any>;
  showTime?: boolean;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
  scheduleStart: string;
}

const AvailabilityPragmatic = ({
  id,
  availability,
  component,
  showTime,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier,
  scheduleStart
}: AvailabilityPragmaticProps) => {
  const { t } = useTranslation('', { keyPrefix: 'activityPlanning' });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !showTime) return;

    const cleanup = dropTargetForElements({
      element: el,
      getData: () => ({
        id,
        planningEntityType,
        planningEntityId,
        planningEntityIdentifier,
        scheduleStart,
        isDroppableCell: true
      }),
      onDragEnter: () => el.setAttribute('data-drag-over', ''),
      onDragLeave: () => el.removeAttribute('data-drag-over'),
      onDrop: () => el.removeAttribute('data-drag-over')
    });

    return () => {
      cleanup();
      el.removeAttribute('data-drag-over');
    };
  }, [
    id,
    planningEntityType,
    planningEntityId,
    planningEntityIdentifier,
    scheduleStart,
    showTime
  ]);

  const availabilityCheck = (
    type: 'AVAILABLE' | 'NOT_AVAILABLE' | 'POSSIBLE' | 'UNSPECIFIED'
  ) => {
    switch (type) {
      case AvailabilityType.NOT_AVAILABLE:
        return {
          backgroundColor: brightness(RED, 90),
          text: 'unavailable'
        };
      default:
        return {
          backgroundColor: '#e8fee8',
          text: ''
        };
    }
  };

  const renderVariables = availabilityCheck(availability.type);

  return (
    <Box
      ref={ref}
      component={component || 'div'}
      display="flex"
      alignItems="center"
      sx={{
        boxSizing: 'border-box',
        color: '#023850',
        backgroundColor: renderVariables.backgroundColor,
        border: `2px solid transparent`,
        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
        borderRadius: '6px',
        width: '100%',
        height: '40px',
        padding: '0 10px',
        '+ *': {
          marginTop: '8px'
        },
        '&[data-drag-over]': {
          color: PRIMARY,
          backgroundColor: brightness(PRIMARY, 90),
          borderColor: PRIMARY
        }
      }}
    >
      {!showTime && renderVariables.text
        ? t(renderVariables.text)
        : t(renderVariables.text + 'FromTil', {
            from: dayjs(availability.start).format('HH:mm'),
            to: dayjs(availability.end).format('HH:mm')
          })}
    </Box>
  );
};

export default memo(AvailabilityPragmatic);
